import { ChevronDown } from 'lucide-react';
import { FC, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopoverDismiss } from '../usePopoverDismiss';
import { useAutoFocusWhenVisible } from 'features/common/hooks/useAutoFocusWhenVisible';
import styles from './PunchupCombobox.module.css';

export interface ComboboxOption {
	value: string | number;
	label: string;
	/** Optional colour swatch rendered before the label (e.g. a tag colour). Pass a concrete
	 * CSS colour; the caller owns any contrast/darkening treatment. */
	color?: string;
	/** Optional muted secondary text rendered after the label. Searchable too — the filter matches
	 * everything the option shows, so the user can type what they can see. */
	description?: string;
	/** Optional extra search terms that are matched but never rendered (e.g. ticket and order numbers). */
	keywords?: string;
	/** Optional group label. Options sharing a group render under one (uppercase) header, in
	 *  first-seen order. Omit on every option for an ungrouped, header-less flat list. */
	group?: string;
}

export interface PunchupComboboxProps {
	options: ComboboxOption[];
	value: string | number | null;
	onChange: (value: string | number) => void;
	/** Id for the trigger button, so a `<label htmlFor>` can name the control. */
	id?: string;
	placeholder?: string;
	searchPlaceholder?: string;
	isLoading?: boolean;
	error?: string;
	/**
	 * Keep the panel open after a selection instead of closing it. Use for "add many" pickers
	 * (e.g. tag assignment) where the chosen option leaves the list and the user typically adds
	 * several in a row. The search term is cleared on each pick either way.
	 */
	keepOpenOnSelect?: boolean;
	/** Message shown when there are no options to choose from / none match the search. */
	emptyLabel?: string;
	/**
	 * Trigger height. `'control'` (default) is the 40px controls-bar row; `'form'` is the 48px
	 * form-field row, so the combobox lines up with the other fields in a Punchup form.
	 */
	size?: 'control' | 'form';
	/**
	 * Readout mode: the trigger carries the native `disabled` attribute (unreachable by pointer and
	 * keyboard) and no panel renders, while the selected label stays readable.
	 */
	disabled?: boolean;
	/**
	 * Tint the trigger as carrying an unsaved change. Suppressed automatically while the field has an
	 * error, while the panel is open, and while disabled — see src/styles/docs/forms.md.
	 */
	dirty?: boolean;
}

// Hard ceiling on panel height so a long list never dominates the viewport.
const MAX_PANEL_HEIGHT = 280;
// Floor so the panel stays usable even when the trigger sits low in the iframe.
const MIN_PANEL_HEIGHT = 120;
const GAP = 4;
const VIEWPORT_MARGIN = 8;

/** Matches everything the option renders — its label plus the muted description beside it — and its keywords. */
const matchesSearch = (option: ComboboxOption, term: string) =>
	option.label.toLowerCase().includes(term) ||
	!!option.description?.toLowerCase().includes(term) ||
	!!option.keywords?.toLowerCase().includes(term);

export const PunchupCombobox: FC<PunchupComboboxProps> = ({
	options,
	value,
	onChange,
	id,
	placeholder = 'Select…',
	searchPlaceholder = 'Search…',
	isLoading = false,
	error,
	keepOpenOnSelect = false,
	emptyLabel = 'No matches',
	size = 'control',
	disabled = false,
	dirty = false,
}) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
	const wrapperRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);

	/* A combobox can become disabled while its panel happens to be open (a refetch that locks the
	 * field), and an open panel would still commit a value. Derive "really open" once. */
	const isOpen = open && !disabled;

	// Focus the portalled search input when the panel opens, deferred a frame so the iframe height
	// loop can't steal it (the shared iframe-safe focus pattern; replaces a raw autoFocus).
	useAutoFocusWhenVisible(searchInputRef, isOpen);

	const selectedOption = options.find((o) => o.value === value) ?? null;

	const filtered = search ? options.filter((o) => matchesSearch(o, search.toLowerCase())) : options;

	// Bucket the filtered options under their `group` label, preserving first-seen order. With no
	// groups set this collapses to a single header-less section — identical to a flat list.
	const groupedOptions: Array<{ label?: string; options: ComboboxOption[] }> = [];
	const groupIndexByLabel = new Map<string | undefined, number>();
	filtered.forEach((option) => {
		const existing = groupIndexByLabel.get(option.group);
		if (existing === undefined) {
			groupIndexByLabel.set(option.group, groupedOptions.length);
			groupedOptions.push({ label: option.group, options: [option] });
		} else {
			groupedOptions[existing].options.push(option);
		}
	});

	/* Anchor the panel below the trigger and clamp its height to the room below, so it never
	 * overflows the height-synced iframe. Deliberately does NOT flip above the trigger (tested as
	 * poor UX). See CLAUDE.md "Iframe-safe placement (open below + clamp)". */
	const updatePanelPosition = () => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
		// Clamp to the room below (so it fits), but keep a usable floor and a sane ceiling.
		const maxHeight = Math.min(Math.max(spaceBelow, MIN_PANEL_HEIGHT), MAX_PANEL_HEIGHT);
		setPanelStyle({ top: rect.bottom + GAP, left: rect.left, width: rect.width, maxHeight });
	};

	/** Close the panel. `focusTrigger` hands focus back to the trigger, so a keyboard user who came
	 *  from it — or whose focused option just unmounted — lands somewhere real. */
	const dismiss = (focusTrigger = false) => {
		setOpen(false);
		setSearch('');
		if (focusTrigger) triggerRef.current?.focus();
	};

	/* Outside click dismisses; scroll/resize RE-ANCHORS the panel rather than closing it — closing on
	 * those events dismissed the dropdown mid-search inside the height-synced iframe (autofocus
	 * scroll-into-view + the body-mutation→height→resize loop). See usePopoverDismiss. */
	usePopoverDismiss({
		open: isOpen,
		wrapperRef,
		panelRef,
		onDismiss: () => dismiss(),
		onReposition: updatePanelPosition,
	});

	const handleToggle = () => {
		if (!open) updatePanelPosition();
		setOpen((v) => !v);
		setSearch('');
	};

	const handleSelect = (option: ComboboxOption) => {
		onChange(option.value);
		if (keepOpenOnSelect) {
			// The picked option leaves the list (the parent updates options); keep the panel open
			// and re-measure since the list got shorter.
			setSearch('');
			updatePanelPosition();
		} else {
			dismiss(true);
		}
	};

	/* Keydowns raised inside the portalled panel reach this wrapper-level handler because React events
	 * bubble through the component tree, not the DOM tree. See CLAUDE.md → Keyboard / ARIA. */
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key !== 'Escape' || !isOpen) return;
		e.stopPropagation();
		dismiss(true);
	};

	// Only nameable when the caller gave the trigger an id — `aria-controls` needs a real target.
	const listboxId = id ? `${id}-listbox` : undefined;

	const panel = isOpen
		? ReactDOM.createPortal(
				<div ref={panelRef} className={styles.panel} style={panelStyle}>
					<input
						ref={searchInputRef}
						type="text"
						className={styles.searchInput}
						placeholder={searchPlaceholder}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
					{/* The listbox is the option list alone — the search input above it is not part of it. */}
					<div id={listboxId} role="listbox" className={styles.list}>
						{filtered.length === 0 ? (
							<p className={styles.empty}>{emptyLabel}</p>
						) : (
							groupedOptions.map((group) => (
								<div
									key={group.label ?? '__ungrouped__'}
									className={styles.group}
									role={group.label ? 'group' : undefined}
									aria-label={group.label}
								>
									{group.label && <div className={styles.groupHeader}>{group.label}</div>}
									{group.options.map((option) => {
										const isSelected = option.value === value;
										return (
											<button
												key={option.value}
												type="button"
												role="option"
												aria-selected={isSelected}
												className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
												/* Selection rides on `click`, so Enter/Space on a focused option works as well as a
												 * pointer. The mousedown is only cancelled to keep the search input focused. */
												onMouseDown={(e) => e.preventDefault()}
												onClick={() => handleSelect(option)}
											>
												{option.color && <span className={styles.swatch} style={{ backgroundColor: option.color }} />}
												<span className={styles.optionLabel}>{option.label}</span>
												{option.description && <span className={styles.optionDescription}>{option.description}</span>}
											</button>
										);
									})}
								</div>
							))
						)}
					</div>
				</div>,
				document.body
		  )
		: null;

	/* The unsaved-change tint is suppressed while the field errors, is open, or is disabled — the
	 * shared field contract in src/styles/docs/forms.md. */
	const showDirtyTint = dirty && !error && !isOpen && !disabled;

	return (
		<div className={styles.wrapper} ref={wrapperRef} onKeyDown={handleKeyDown}>
			<button
				ref={triggerRef}
				id={id}
				type="button"
				data-testid="combobox-trigger"
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={isOpen ? listboxId : undefined}
				className={[
					styles.trigger,
					size === 'form' ? styles.triggerForm : null,
					isOpen ? styles.triggerOpen : null,
					error ? styles.triggerError : null,
					showDirtyTint ? styles.triggerDirty : null,
				]
					.filter(Boolean)
					.join(' ')}
				onClick={handleToggle}
				disabled={isLoading || disabled}
			>
				<span className={selectedOption ? undefined : styles.placeholder}>
					{isLoading ? 'Loading…' : selectedOption?.label ?? placeholder}
				</span>
				<ChevronDown size={16} className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
			</button>
			{panel}
			{error && <p className={styles.errorText}>{error}</p>}
		</div>
	);
};
